import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { J, MANROPE, FOCUS, SORTS, sortRows } from "./journeyTokens";
import { JourneyRow } from "./JourneyRow";
import { JourneyPanel } from "./JourneyPanel";
import { StartJourneyDialog } from "./StartJourneyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CELLS = [
  { key: "onboarding", label: "ONBOARDING", color: J.ink, click: null },
  { key: "next_step_overdue", label: "NEXT STEP OVERDUE", color: J.overdue, click: "overdue", sub: "clients" },
  { key: "past_target", label: "PAST TARGET", color: J.overdue, click: "past_target", sub: "not live yet" },
  { key: "no_csm", label: "NO CSM", color: J.warnInk, click: "no_csm" },
  { key: "no_target", label: "NO TARGET DATE", color: J.warnInk, click: "no_target" },
  { key: "hours_logged", label: "HOURS LOGGED", color: J.ink, click: null, sub: "this onboarding cohort" },
];

export default function JourneyPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v && v !== "all" ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const q = params.get("q") || "";
  const track = params.get("track") || "all";
  const quick = params.get("quick");
  const sort = params.get("sort") || "target";
  const showLive = params.get("live") === "1";
  const openId = params.get("client");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/customer-success/journeys/register`);
      setData(r.data);
    } catch { toast.error("Could not load the journeys"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const today = data?.today || new Date().toISOString().slice(0, 10);
  const all = data?.journeys || [];

  const rows = useMemo(() => {
    let list = all.filter((r) => (showLive ? true : !r.is_live));
    if (track !== "all") list = list.filter((r) => r.track === track.toUpperCase());
    if (quick === "overdue") list = list.filter((r) => r.next_step_overdue);
    if (quick === "past_target") list = list.filter((r) => r.past_target);
    if (quick === "no_csm") list = list.filter((r) => !r.csm && !r.is_live);
    if (quick === "no_target") list = list.filter((r) => !r.target_go_live && !r.is_live);
    if (q) {
      const t = q.toLowerCase();
      list = list.filter((r) => `${r.company_name} ${r.client_id}`.toLowerCase().includes(t));
    }
    return sortRows(list, sort);
  }, [all, showLive, track, quick, q, sort]);

  const opened = all.find((r) => r.client_id === openId) || null;
  const filtersActive = track !== "all" || !!quick || !!q;
  const trackCount = (k) => all.filter((r) => !r.is_live && (k === "all" || r.track === k)).length;

  return (
    <div className="space-y-3.5" data-testid="journey-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: J.ink }}>Journey</h1>
          <p className="text-[13px]" style={{ color: J.ink2 }}>
            Onboarding from kickoff to hand-over. <span className="mono text-[11.5px]" style={{ color: J.ink }}>{s?.onboarding ?? "—"}</span> clients
            onboarding, <span className="mono text-[11.5px]" style={{ color: J.ink }}>{s?.next_step_overdue ?? "—"}</span> with an overdue next step.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0" style={{ whiteSpace: "nowrap" }}>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" style={{ color: J.ink2 }}>
            <span onClick={() => set({ live: showLive ? null : "1" })} data-testid="journey-show-live"
              style={{ width: 34, height: 18, borderRadius: 999, position: "relative", display: "inline-block",
                background: showLive ? J.accentInk : "#C9C3B6", transition: "background 160ms" }}>
              <span style={{ position: "absolute", top: 2, left: showLive ? 18 : 2, width: 14, height: 14,
                borderRadius: 999, background: showLive ? J.accent : "#fff", transition: "left 160ms" }} />
            </span>
            Show live <span className="mono text-[11.5px]" style={{ color: J.muted }}>{s?.live ?? 0}</span>
          </label>
          <button onClick={() => setStartOpen(true)} data-testid="journey-start"
            className="h-9 px-3.5 rounded-[8px] text-[12px] font-bold uppercase inline-flex items-center gap-1.5"
            style={{ background: J.accent, border: `1px solid ${J.accentEdge}`, color: J.accentInk }}>
            <Plus className="w-3.5 h-3.5" /> Start journey
          </button>
        </div>
      </div>

      <div className="rounded-[12px] overflow-hidden" data-testid="journey-summary-strip"
        style={{ background: J.card, border: `1px solid ${J.edge}`,
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        {CELLS.map((c, i) => {
          const on = quick === c.click || (!quick && c.key === "onboarding");
          const sub = c.key === "onboarding" ? `${s?.lite ?? 0} Lite · ${s?.guided ?? 0} Guided`
            : c.key === "no_csm" || c.key === "no_target" ? `of ${s?.onboarding ?? 0}` : c.sub;
          return (
            <button key={c.key} disabled={!c.click && c.key !== "onboarding"} data-testid={`journey-strip-${c.key}`}
              onClick={() => set({ quick: quick === c.click ? null : c.click })}
              className="text-left px-4 py-3 relative disabled:cursor-default"
              style={{ borderLeft: i === 0 ? "none" : `1px solid ${J.rule}`, background: on ? "#FFFBEA" : undefined }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.12em", color: J.muted }}>{c.label}</div>
              <div style={{ ...MANROPE, fontSize: 26, fontWeight: 800, color: (s?.[c.key] ?? 0) > 0 ? c.color : J.ink }}>
                {s ? s[c.key] : "—"}
              </div>
              <div className="text-[12px]" style={{ color: J.muted }}>{sub}</div>
              {on && <span className="absolute left-0 right-0 bottom-0" style={{ height: 3, background: J.accent }} />}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: J.muted }} />
            <input value={q} onChange={(e) => set({ q: e.target.value })} placeholder="Search onboarding clients"
              className={`h-[34px] w-[260px] max-w-full rounded-[8px] pl-8 pr-2.5 text-[13px] bg-white ${FOCUS}`}
              style={{ border: `1px solid ${J.edge}`, color: J.ink }} data-testid="journey-search" />
          </div>
          <div className="flex rounded-[8px] overflow-hidden" style={{ border: `1px solid ${J.edge}` }}>
            {[["all", "All"], ["lite", "Lite"], ["guided", "Guided"]].map(([k, l]) => (
              <button key={k} onClick={() => set({ track: k })} data-testid={`journey-track-${k}`}
                className="h-[32px] px-3 text-[12.5px] inline-flex items-center gap-1.5"
                style={track === k ? { background: J.accentInk, color: "#fff", fontWeight: 600 }
                  : { background: "#fff", color: J.ink2 }}>
                {l} <span className="mono text-[11px]" style={{ color: track === k ? J.accent : J.muted }}>
                  {trackCount(k === "all" ? "all" : k.toUpperCase())}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: J.muted }}>SORT</span>
          <select value={sort} onChange={(e) => set({ sort: e.target.value })} data-testid="journey-sort"
            className="h-[34px] rounded-[8px] px-2.5 text-[13px] bg-white" style={{ border: `1px solid ${J.edge}`, color: J.ink }}>
            {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {filtersActive && (
        <div className="flex items-center gap-3 text-[12.5px]" data-testid="journey-filter-summary">
          <span style={{ color: J.ink2 }}><span className="mono">{rows.length}</span> of <span className="mono">{s?.onboarding ?? 0}</span> clients</span>
          <button onClick={() => setParams(new URLSearchParams(showLive ? { live: "1" } : {}), { replace: true })}
            className="font-semibold hover:underline" style={{ color: "#8A6D00" }} data-testid="journey-clear-filters">Clear filters</button>
        </div>
      )}

      <div className="rounded-[12px]" style={{ background: J.card, border: `1px solid ${J.edge}` }}>
        <div className="journey-row journey-head" style={{ gap: 16, padding: "9px 18px", background: J.head,
          fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: J.muted, borderRadius: "12px 12px 0 0" }}>
          <span className="jr-client">CLIENT</span><span className="jr-track">TRACK</span>
          <span className="jr-stage">STAGE</span><span className="jr-target">TARGET GO-LIVE</span>
          <span className="jr-hours">HOURS</span><span className="jr-training">TRAINING</span>
          <span className="jr-next">NEXT STEP</span><span className="jr-chev" />
        </div>
        {loading ? [0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="animate-pulse" style={{ height: 68, borderTop: `1px solid ${J.rule}`, background: "#FBFAF7" }} />
        )) : rows.length === 0 ? (
          <div className="px-5 py-10 text-center" data-testid="journey-empty">
            <div className="text-[14px] font-semibold" style={{ color: J.ink }}>
              {filtersActive ? "No clients match these filters." : "No clients are onboarding."}
            </div>
            {!filtersActive && (
              <button onClick={() => setStartOpen(true)} className="mt-3 h-9 px-3.5 rounded-[8px] text-[12px] font-bold uppercase"
                style={{ background: J.accent, border: `1px solid ${J.accentEdge}`, color: J.accentInk }}
                data-testid="journey-empty-start">Start journey</button>
            )}
          </div>
        ) : rows.map((r) => (
          <JourneyRow key={r.client_id} r={r} today={today} open={openId === r.client_id}
            onOpen={(x) => set({ client: x.client_id })} />
        ))}
      </div>

      {opened && (
        <JourneyPanel r={opened} today={today} csmOptions={data?.csm_options || []}
          onClose={() => set({ client: null })} onSaved={load} onError={(m) => toast.error(m)} />
      )}

      {startOpen && (
        <StartJourneyDialog
          existingIds={new Set(all.map((r) => (r.client_id || "").toUpperCase()))}
          onClose={() => setStartOpen(false)}
          onStarted={(c) => {
            setStartOpen(false);
            toast.success(`${c.company_name} is now onboarding`);
            load();
            set({ client: c.client_id });
          }}
          onError={(m) => toast.error(m)} />
      )}
    </div>
  );
}
